import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService, TranslationService, UserIdService } from '@spartacus/core';
import { catchError, map, Observable, switchMap } from 'rxjs';
import { failStatus, StatusRequest, successStatus } from '../../../../core/models/request-status.model';
import {
  AccountRequisition,
  BaseRequisition,
  RegisterRequisition,
  ReplyConfig,
  REQUEST_STATUS,
  REQUEST_TYPE,
  RequisitionsRequest,
  RequistionDetailsRequest,
  RoleRequisition,
} from './approvals-page.model';

@Injectable({
  providedIn: 'root',
})
export class ApprovalsPageConnector {
  constructor(
    private http: HttpClient,
    private occEndPointService: OccEndpointsService,
    private translationService: TranslationService,
    private userIdService: UserIdService,
  ) {}

  getRequestList(): Observable<RequisitionsRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('myRequests', { urlParams: { userId } });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              requests: response,
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => {
                return {
                  message: textError,
                  ...failStatus,
                };
              }),
            );
          }),
        );
      }),
    );
  }

  getRequestDetails(codeRequest: string): Observable<RequistionDetailsRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('requestsDetails', { urlParams: { userId, codeRequest } });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              ...this.normalizeRequestDetails(response),
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }

  replyRequest(replyConfig: ReplyConfig): Observable<{ hasUpdated?: boolean } & StatusRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('replyRequest', {
          urlParams: { userId, codeRequest: replyConfig.code, status: replyConfig.status },
        });

        let body = {};

        if (replyConfig.type === REQUEST_TYPE.REGISTER) {
          body = replyConfig.registerData!;
        }

        return this.http.post(url, body).pipe(
          map((response: any) => {
            return {
              hasUpdated: response?.success ?? false,
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }

  normalizeRequestDetails(reponse: any): RequistionDetailsRequest {
    const getType = (type: string): REQUEST_TYPE => {
      switch (type) {
        case 'Registration Request':
          return REQUEST_TYPE.REGISTER;
        case 'Account Request':
          return REQUEST_TYPE.ACCOUNT;
        case 'Role Request':
          return REQUEST_TYPE.ROLE;
        default:
          return REQUEST_TYPE.UNDEFINED;
      }
    };

    const getStatus = (status: string): REQUEST_STATUS => {
      switch (status) {
        case 'Reproved':
          return REQUEST_STATUS.REPROVED;
        case 'Pending':
          return REQUEST_STATUS.PENDING;
        case 'Approved':
          return REQUEST_STATUS.APPROVED;
        default:
          return REQUEST_STATUS.UNDEFINED;
      }
    };

    const getBaseInfo = (baseDetails: any): BaseRequisition => {
      return {
        code: baseDetails?.code || '',
        user: baseDetails?.user || '',
        date: baseDetails?.date || '',
        status: getStatus(baseDetails?.status),
        type: getType(reponse.type),
        approvalDate: baseDetails?.approvalDate || '',
        approvalUser: baseDetails?.approvalUser || '',
      };
    };

    const getRoleRequestDetails = (roleDetails: any): RoleRequisition => {
      return {
        ...getBaseInfo(roleDetails),
        roleRequest: {
          previousUserGroup: roleDetails?.roleRequest?.previousUserGroup || '',
          userGroup: roleDetails?.roleRequest?.userGroup || '',
          linkedAccounts: roleDetails.roleRequest.linkedAccounts || [],
        },
      };
    };

    const getAccountRequestDetails = (accountDetails: any): AccountRequisition => {
      return {
        ...getBaseInfo(accountDetails),
        accountRequest: {
          linkedAccounts: accountDetails?.linkedAccounts || [],
          account: accountDetails?.accountRequest?.account || '',
          reason: accountDetails?.accountRequest?.reason || '',
        },
      };
    };

    const getRegisterRequestDetails = (registerDetails: any): RegisterRequisition => {
      return {
        ...getBaseInfo(registerDetails),
        registrationRequest: {
          ...(registerDetails?.registrationRequest || {}),
        },
      };
    };

    return {
      type: getType(reponse.type),
      roleRequest: getType(reponse.type) === REQUEST_TYPE.ROLE ? getRoleRequestDetails(reponse) : undefined,
      accountRequest: getType(reponse.type) === REQUEST_TYPE.ACCOUNT ? getAccountRequestDetails(reponse) : undefined,
      registerRequest: getType(reponse.type) === REQUEST_TYPE.REGISTER ? getRegisterRequestDetails(reponse) : undefined,
    };
  }
}
